import {
  faCheck,
  faMapMarkerAlt,
  faSort,
} from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import classNames from 'classnames'
import { FC, useEffect, useRef, useState } from 'react'
import styles from './scopeSelect.module.scss'

export interface ScopeOption {
  id: string
  name: string
}

interface ScopeSelectProps {
  // "All Facilities" / "All Locations"
  allLabel: string
  className?: string
  // plural noun for "3 facilities"
  noun: string
  onChange: (ids: string[]) => void
  options: ScopeOption[]
  // [] = all
  selected: string[]
}

/**
 * The design's widget filter: "All Facilities · All selected" pill with a
 * checkbox list. An empty selection means every option; ticking every option
 * collapses back to it.
 */
export const ScopeSelect: FC<ScopeSelectProps> = ({
  allLabel,
  className,
  noun,
  onChange,
  options,
  selected,
}) => {
  const [open, setOpen] = useState(false)
  const ref = useRef<HTMLDivElement>(null)

  useEffect(() => {
    if (!open) return undefined
    const close = (event: MouseEvent) => {
      if (ref.current && !ref.current.contains(event.target as Node)) {
        setOpen(false)
      }
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setOpen(false)
    }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', escape)
    return () => {
      document.removeEventListener('mousedown', close)
      document.removeEventListener('keydown', escape)
    }
  }, [open])

  const picked = selected.filter((id) => options.some((o) => o.id === id))
  const allSelected = picked.length === 0 || picked.length === options.length
  const label = allSelected
    ? allLabel
    : picked.length === 1
      ? options.find((o) => o.id === picked[0])?.name ?? `1 ${noun}`
      : `${picked.length} ${noun}`

  const toggle = (id: string) => {
    const current = allSelected ? [] : picked
    const next = current.includes(id)
      ? current.filter((x) => x !== id)
      : [...current, id]
    onChange(next.length === options.length ? [] : next)
  }

  return (
    <div className={classNames(styles.select, className)} ref={ref}>
      <button
        aria-expanded={open}
        aria-haspopup="listbox"
        className={classNames(styles.button, open && styles.buttonOpen)}
        onClick={() => setOpen((v) => !v)}
        type="button"
      >
        <FontAwesomeIcon className={styles.pin} icon={faMapMarkerAlt} />
        <span className={styles.label}>{label}</span>
        <span className={styles.badge}>
          {allSelected ? 'All selected' : `${picked.length} selected`}
        </span>
        <FontAwesomeIcon className={styles.caret} icon={faSort} />
      </button>
      {open && (
        <ul aria-multiselectable className={styles.menu} role="listbox">
          <li aria-selected={allSelected} role="option">
            <button
              className={styles.option}
              onClick={() => onChange([])}
              type="button"
            >
              <span
                className={classNames(
                  styles.checkbox,
                  allSelected && styles.checkboxOn
                )}
              >
                {allSelected && <FontAwesomeIcon icon={faCheck} />}
              </span>
              {allLabel}
            </button>
          </li>
          {options.map((option) => {
            const on = !allSelected && picked.includes(option.id)
            return (
              <li aria-selected={on} key={option.id} role="option">
                <button
                  className={styles.option}
                  onClick={() => toggle(option.id)}
                  type="button"
                >
                  <span
                    className={classNames(
                      styles.checkbox,
                      on && styles.checkboxOn
                    )}
                  >
                    {on && <FontAwesomeIcon icon={faCheck} />}
                  </span>
                  <span className={styles.optionLabel}>{option.name}</span>
                </button>
              </li>
            )
          })}
        </ul>
      )}
    </div>
  )
}
